import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Edit, InfoOutlined } from "@mui/icons-material";
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import type {
	ColDef,
	GridApi,
	IDetailCellRendererParams,
	RowClassParams,
	RowStyle,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import type { IncentiveReportSummary } from "../../models/IncentiveRow";
import type { IncentiveRowWithEventSnapshots } from "../../models/ShiftIncentiveEvent";
import { ROW_TINTS, SURFACE } from "../../constants/colors";
import { ShiftIncentiveEventsPanel } from "../../shared/components/ShiftIncentiveEventsPanel";

const formatProductionDate = (value: string | null | undefined) => {
	if (!value) return "";

	const date = String(value).substring(0, 10);
	const [year, month, day] = date.split("-");

	return `${month}/${day}/${year}`;
};

const TargetHeader = () => (
	<Box
		component="span"
		sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
	>
		<span>Target</span>
		<Tooltip
			arrow
			title="Target is calculated from events scheduled at shift start"
		>
			<InfoOutlined
				aria-label="Target calculation information"
				sx={{ fontSize: 15, color: "primary.main", cursor: "help" }}
			/>
		</Tooltip>
	</Box>
);

const IncentiveManagerBreakdownDetail = ({
	data,
}: IDetailCellRendererParams<IncentiveRowWithEventSnapshots>) => {
	if (!data?.eventSnapshots?.length) return null;

	return (
		<Box sx={{ p: 1.5, backgroundColor: SURFACE.background }}>
			<ShiftIncentiveEventsPanel
				rows={data.eventSnapshots}
				isFutureShift={data.IsFutureShift}
			/>
		</Box>
	);
};

type Props = {
	rows: IncentiveRowWithEventSnapshots[];
	summary: IncentiveReportSummary | undefined;
	isLoading: boolean;
	canEdit: boolean;
	onEdit: (row: IncentiveRowWithEventSnapshots) => void;
	onGridReady: (api: GridApi) => void;
};

export const IncentiveManagerGrid = ({
	rows,
	summary,
	isLoading,
	canEdit,
	onEdit,
	onGridReady,
}: Props) => {
	const { defaultColDef } = useGridDefinitions({});

	const columnDefs = useMemo(
		(): ColDef<IncentiveRowWithEventSnapshots>[] => [
			{
				headerName: "Date",
				field: "ProductionDate",
				minWidth: 120,
				sort: "asc",
				cellRenderer: "agGroupCellRenderer",
				cellRendererParams: {
					suppressCount: true,
					innerRenderer: (params: { value: string }) => {
						return formatProductionDate(params.value);
					},
				},
				valueFormatter: (params) => formatProductionDate(params.value),
			},
			{
				headerName: "Crew",
				field: "Crew",
				minWidth: 40,
				cellStyle: { textAlign: "center" },
			},
			{
				headerName: "Shift",
				field: "Shift",
				minWidth: 40,
				cellStyle: { textAlign: "center" },
			},
			{
				headerName: "Target",
				field: "DisplayedTarget",
				width: 100,
				headerComponentParams: {
					innerHeaderComponent: TargetHeader,
				},
				cellStyle: { textAlign: "right" },
				valueFormatter: (params) =>
					params.value != null ? params.value.toFixed(0) : "—",
			},
			{
				headerName: "Finished",
				field: "DisplayedFinished",
				minWidth: 80,
				cellStyle: { textAlign: "right" },
				valueFormatter: (params) =>
					params.value != null ? params.value.toFixed(0) : "—",
			},
			{
				headerName: "Adj.",
				field: "Adjustment",
				minWidth: 40,
				cellStyle: { textAlign: "right" },
				valueFormatter: (params) => {
					const v = params.value;
					if (v === 0 || v == null) return "—";
					return v > 0 ? `+${v}` : String(v);
				},
			},
			{
				headerName: "Hrs",
				field: "HoursWorked",
				minWidth: 40,
				cellStyle: { textAlign: "right" },
				valueFormatter: (params) => (params.value ? String(params.value) : "—"),
			},
			{
				headerName: "Inc%",
				field: "IncPercent",
				minWidth: 40,
				cellStyle: { textAlign: "right" },
				valueFormatter: (params) =>
					params.value != null ? `${params.value.toFixed(1)}%` : "—",
			},
			{
				headerName: "Override",
				field: "TargetTonsOverride",
				minWidth: 80,
				cellStyle: { textAlign: "right" },
				valueFormatter: (params) =>
					params.value != null ? params.value.toFixed(0) : "—",
			},
			{
				headerName: "Override By",
				field: "LastOverrideBy",
				minWidth: 140,
				valueFormatter: (params) => params.value ?? "—",
			},
			{
				headerName: "Override At",
				field: "LastOverrideAt",
				minWidth: 140,
				valueFormatter: (params) => {
					if (!params.value) return "—";
					return dxtToLocalServerTime(params.value, "MM/dd/yyyy HH:mm") || "—";
				},
			},
			{
				headerName: "Notes",
				field: "Notes",
				flex: 1,
				minWidth: 600,
				cellStyle: {
					display: "flex",
					justifyContent: "flex-start",
					paddingLeft: "12px",
				},
			},
			...(canEdit
				? [
						{
							headerName: "",
							width: 50,
							sortable: false,
							filter: false,
							resizable: false,
							cellClass: "incentive-cell-edit",
							cellRenderer: (params: {
								data: IncentiveRowWithEventSnapshots;
							}) => {
								const row = params.data;
								if (row.IsFutureShift || row.TargetTons == null) return null;

								return (
									<IconButton
										size="small"
										title="Edit shift override"
										onClick={() => onEdit(row)}
									>
										<Edit fontSize="inherit" color="primary" />
									</IconButton>
								);
							},
							cellStyle: {
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							} as const,
						} satisfies ColDef<IncentiveRowWithEventSnapshots>,
					]
				: []),
		],
		[canEdit, onEdit],
	);

	const getRowStyle = (
		params: RowClassParams<IncentiveRowWithEventSnapshots>,
	): RowStyle | undefined => {
		if (params.data?.IsCurrentShift === true) {
			return { backgroundColor: ROW_TINTS.current };
		}

		if (params.data?.TargetTonsOverride != null) {
			return { backgroundColor: ROW_TINTS.override };
		}

		return undefined;
	};

	return (
		<Paper
			elevation={2}
			sx={{
				height: "calc(100vh - 240px)",
				minHeight: "calc(100vh - 240px)",
				p: "10px",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<AgGridReact<IncentiveRowWithEventSnapshots>
					masterDetail
					detailRowAutoHeight
					loading={isLoading}
					rowData={rows}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					isRowMaster={(data) => !!data?.hasEventSnapshots}
					detailCellRenderer={IncentiveManagerBreakdownDetail}
					getRowStyle={getRowStyle}
					animateRows
					rowHeight={38}
					headerHeight={35}
					onGridReady={(params) => onGridReady(params.api)}
					loadingOverlayComponent={CenteredLazyLoading}
					suppressContextMenu
				/>
			</Box>
			<Box display="flex" justifyContent="space-between" gap={3} pt={1} px={2}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Box
						sx={{
							width: 14,
							height: 14,
							borderRadius: "2px",
							backgroundColor: ROW_TINTS.override,
							border: `1px solid ${SURFACE.border}`,
							flexShrink: 0,
						}}
					/>
					<Typography variant="body2" color="text.secondary">
						Target override applied
					</Typography>
				</Box>
				{summary && (
					<Box display="flex" justifyContent="flex-end" gap={3}>
						<Typography variant="body2" color="text.secondary">
							<strong>Total Target:</strong>{" "}
							{summary.TotalTarget != null
								? summary.TotalTarget.toFixed(0)
								: "—"}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							<strong>Total Finished:</strong>{" "}
							{summary.TotalFinished != null
								? summary.TotalFinished.toFixed(0)
								: "—"}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							<strong>Avg Inc%:</strong>{" "}
							{summary.AvgIncPercent != null
								? `${summary.AvgIncPercent.toFixed(1)}%`
								: "—"}
						</Typography>
					</Box>
				)}
			</Box>
		</Paper>
	);
};
